/**
 * Paying.
 *
 * `kiosk-payment-method-modal.tsx` was 1,177 lines — the largest file in the
 * Electron tree — and it was method selection, terminal state, QPay state,
 * e-Barimt company entry and split payment in one component. Splitting it is
 * the one piece of UI work in this redesign that is not a port.
 *
 * What each part owes the customer: **say what is unavailable before they
 * choose it.** A QPay button that greys out with a reason beats one that fails
 * after the customer has committed.
 *
 * **The methods are rows now, and they are in the bottom half.** They were
 * three 250px cards in a `justify-center` column, which on a 1920px panel is a
 * laptop's worth of screen floating in the middle with 600px of nothing above
 * and below it — and the three targets a customer has come to this screen to
 * press sat above the height a hand comfortably reaches. A full-width row
 * gives each mark four times the area and puts all three inside the band.
 */
import { useEffect, useMemo, useState } from 'react'
import { format, totalsOf } from '@mgc/domain'
import cashArt from '@/assets/payment-methods/cash.jpg'
import cardArt from '@/assets/payment-methods/golomt.png'
import qpayArt from '@/assets/payment-methods/qpay.jpeg'
import { ActionBar, AmountPill, Button, Notice, Spinner } from '@/components/ui'
import { Icon } from '@/components/icons'
import { useRefusal, useT } from '@/i18n'
import { RemovedNotice } from '@/components/removed-notice'
import { holdUpdates } from '@/shell/update'
import { inShell, shellInfo } from '@/shell/native'
import { useKiosk, usePricedCart } from '@/store/kiosk'
import { useSession } from '@/store/session'
import { abandonOrder, cancelQPay, payByCard, payByCash, startQPay } from '@/services/pay'
import type { MoneyTaken, PayContext } from '@/services/pay'
import { CompanyReceipt } from './company-receipt'
import { Coupon } from './coupon'
import { QPayModal } from './qpay-modal'
import { TerminalModal } from './terminal-modal'

/**
 * The mark a customer recognises before they have read the label.
 *
 * Restored from the Electron app's `getPaymentMethodImage`, remapped: it keyed
 * off 1C's payment type codes and carried `standalone_terminal` and
 * `integrated_terminal` separately to the same picture, which is a distinction
 * the customer was never shown. Three methods, three marks.
 */
const METHOD_ART: Record<'cash' | 'card' | 'qpay', string> = {
  cash: cashArt,
  card: cardArt,
  qpay: qpayArt
}

const uuid = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`

export function Payment() {
  const t = useT()
  const refusalText = useRefusal()
  const { lines } = usePricedCart()
  const branch = useSession((s) => s.branch)
  const online = useKiosk((s) => s.online)
  const setStep = useKiosk((s) => s.setStep)
  const pendingOrderId = useKiosk((s) => s.pendingOrderId)
  const setPendingOrderId = useKiosk((s) => s.setPendingOrderId)
  const setOrderId = useKiosk((s) => s.setOrderId)
  const setCapture = useKiosk((s) => s.setCapture)
  const setRefusal = useKiosk((s) => s.setRefusal)

  const [company, setCompany] = useState<PayContext['company']>(null)
  const [coupon, setCoupon] = useState<string | null>(null)
  const [busy, setBusy] = useState<'card' | 'cash' | 'qpay' | null>(null)
  const [terminalOpen, setTerminalOpen] = useState(false)
  const [qpay, setQpay] = useState<{ qrText: string; paymentId: string } | null>(null)
  /** Whether the QR on screen can still be paid. The sheet owns its clock. */
  const [qpayLive, setQpayLive] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  /**
   * What the server actually said, under the translated line.
   *
   * The code has a customer-facing sentence and deliberately says little —
   * «Захиалгыг баталгаажуулах боломжгүй байна» is right for the person paying
   * and useless to the attendant they are about to walk over to. 1C's own
   * words go underneath, small, so the person who can act on them can read them
   * off the screen without opening a log.
   */
  const [reason, setReason] = useState<string | null>(null)
  const [moneyTaken, setMoneyTaken] = useState<MoneyTaken>('no')
  const [hasTerminal, setHasTerminal] = useState(false)

  const totals = useMemo(() => totalsOf({ lines, payments: [] }), [lines])

  /** The order id is minted here, once, and is the idempotency key for everything below. */
  const [context] = useState<PayContext>(() => ({
    orderId: uuid(),
    orderType: useKiosk.getState().cart.orderType ?? 'eat_in',
    lines: [],
    company: null,
    placedAt: new Date().toISOString()
  }))

  const payContext: PayContext = { ...context, lines, company, ...(coupon === null ? {} : { coupon }) }

  useEffect(() => {
    void shellInfo().then((info) => setHasTerminal((info?.terminals ?? []).length > 0))
  }, [])

  // Hold every bundle swap for as long as this screen is on. A reload between
  // an approval and the POST is money taken for an order nobody has.
  useEffect(() => {
    holdUpdates('paymentInFlight', true)
    return () => holdUpdates('paymentInFlight', false)
  }, [])

  /*
   * Tell the store while a charge is actually outstanding.
   *
   * The gate above is deliberately coarser — it covers the whole screen — and
   * the idle timer cannot use it: a customer who arrives here and wanders off
   * has to be cleared like any other abandoned basket. What must never be
   * cleared is a customer whose card is in the terminal, and `busy` is the only
   * thing that knows the difference. Cleared on unmount as well, so a screen
   * torn down mid-charge cannot leave the kiosk unable to ever time out.
   */
  const setPaying = useKiosk((s) => s.setPaying)
  /*
   * A live QR is a charge outstanding too. `busy` clears the moment the code is
   * drawn, and a customer paying on their phone does not touch the panel — so
   * the idle guard used to fire mid-payment, abandon the order and release its
   * 1C reservation while the customer was confirming in their banking app. An
   * *expired* code is not held: that customer may well have walked off.
   */
  const charging = busy !== null || (qpay !== null && qpayLive)
  useEffect(() => {
    setPaying(charging)
    return () => setPaying(false)
  }, [charging, setPaying])

  const methods = branch?.paymentMethods ?? [
    { id: 'cash', method: 'cash' as const, label: t('payment.cash'), sortOrder: 1 }
  ]

  /*
   * A card was the one method that stayed available with the server down, on
   * the grounds that the terminal charges locally and the outbox catches the
   * sale. It does — but nothing else does: the order reaches no attendant
   * screen, no order number and no 1C, so the shop cannot make the drink. The
   * kiosk stops selling and says where to go instead.
   *
   * This is the *pre-emptive* half, and it is a memory of the last call that
   * reached the server. `payByCard` asks again for real, immediately before the
   * terminal — see `serverReachable`.
   */
  const unavailable = (method: 'cash' | 'card' | 'qpay'): string | null => {
    if (!online) return t('payment.offline')
    if (method === 'card' && !inShell()) return t('payment.notConfigured')
    if (method === 'card' && !hasTerminal) return t('payment.notConfigured')
    return null
  }

  const finish = (orderId: string) => {
    // Paid. The reservation belongs to the sale now, so nothing may hand it
    // back — `orders.abandon` refuses a paid order anyway, and this keeps the
    // idle timer from even asking.
    setPendingOrderId(null)
    setOrderId(orderId)
    setStep('success')
  }

  const onCash = async () => {
    setBusy('cash')
    setProblem(null)
    setReason(null)
    // From here the server has an order and 1C may be holding stock for it.
    setPendingOrderId(context.orderId)
    const result = await payByCash(payContext, uuid(), totals.gross)
    setBusy(null)
    if (result.ok) {
      setCapture(result.capture)
      return finish(context.orderId)
    }
    setProblem(result.refusal)
    setReason(result.reason ?? null)
    setRefusal(result.refusal)
  }

  const onCard = async (terminalId: string) => {
    setBusy('card')
    setProblem(null)
    setReason(null)
    // From here the server has an order and 1C may be holding stock for it.
    setPendingOrderId(context.orderId)
    const result = await payByCard(payContext, terminalId, uuid())
    setBusy(null)
    setTerminalOpen(false)
    if (result.ok) {
      // Before the step changes: the success screen prints from this the moment
      // it mounts, and offline it is the only description of the charge there is.
      setCapture(result.capture)
      return finish(context.orderId)
    }
    setProblem(result.refusal)
    setReason(result.reason ?? null)
    setMoneyTaken(result.moneyTaken)
  }

  const onQPay = async () => {
    setBusy('qpay')
    setProblem(null)
    setReason(null)
    // From here the server has an order and 1C may be holding stock for it.
    setPendingOrderId(context.orderId)
    const paymentId = uuid()
    const result = await startQPay(payContext, paymentId)
    setBusy(null)
    if (!result.ok) {
      setProblem(result.refusal)
      setReason(result.reason)
      return
    }
    setQpay({ qrText: result.qrText, paymentId })
  }

  return (
    <div className="flex h-full flex-col bg-[var(--color-paper)]">
      {/*
        The amount, in the band that is for reading, on the dark it has been on
        since the basket bar. It is the last thing the customer agreed to and
        the first thing this screen has to say.
      */}
      <header className="flex shrink-0 flex-col items-center gap-6 px-10 pt-14 pb-10">
        <h1 className="display text-[length:var(--text-kiosk-xl)] uppercase">{t('payment.title')}</h1>

        <p className="eyebrow">{t('payment.due')}</p>
        <AmountPill size="large" className="shadow-[var(--shadow-tile)]">
          {format(totals.gross)}
        </AmountPill>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-contain px-10 pb-8">
        {/*
          Offline, said once and up front. Every other screen that can be
          reached without a uplink says so — `welcome` and `menu` both carry the
          strip — and this one did not, so the customer met it as three greyed
          rows and had to infer the cause from the absence. A refusal that has
          already happened outranks it: two banners about the same uplink is
          noise, and the refusal is the one carrying an instruction.
        */}
        {!online && problem === null && <Notice tone="warn">{t('payment.offlineBody')}</Notice>}

        {/*
          Last chance to say it. Past this screen the customer has paid, and a
          line that vanished from the basket after they read the amount is a
          receipt they will dispute at the counter.
        */}
        <RemovedNotice />

        {problem !== null && (
          <Notice tone="bad">
            {/*
              Three outcomes, three messages. Telling a customer whose card may
              have been charged that their payment "failed" is how a shop takes
              money and hands over nothing, and it is the message this screen
              used to show — because the shell could only say yes or no.
            */}
            {moneyTaken === 'maybe' ? (
              <>
                <strong>{t('recovery.unresolved')}</strong> — {t('recovery.unresolvedBody')}{' '}
                {t('recovery.callAttendant')}
              </>
            ) : moneyTaken === 'yes' ? (
              // The card was charged and the server refused the order. The claim
              // is in the outbox and the banner does not go away.
              <>
                <strong>{t('recovery.title')}</strong> — {t('recovery.body')} ({problem})
              </>
            ) : (
              // Named, not raw: this used to render the kebab-case code itself,
              // so a customer read `no-shift` off a kiosk.
              <>
                {refusalText(problem)}
                {reason !== null && (
                  /*
                   * The server's own words, for the person who can act on them.
                   * Small and secondary — a customer does not need «нөөц
                   * хүрэлцэхгүй: Латте», and the attendant they fetch does.
                   * Without this it lives only in `logs/dev.log` and a console
                   * nobody standing at the kiosk can open.
                   */
                  <span className="mt-1 block text-[length:var(--text-kiosk-sm)] opacity-70">{reason}</span>
                )}
              </>
            )}
          </Notice>
        )}

        {/*
          Rows, not cards. A method is a picture the customer recognises before
          they have read anything, and a row across 1080px gives that picture
          160px of edge next to a name in the same type the rest of the flow
          uses. `mt-auto` pins the stack to the foot of the scroller so the
          targets sit together in the reach band whether the branch offers one
          method or three — and the company-receipt control comes down with
          them, because it is a control and the top of the panel is not where
          those go.
        */}
        <div className="mt-auto flex flex-col gap-5">
          {/*
            The extras, as a pair of half-width tiles. Full-width they read as
            two more ways to pay; side by side and quieter than the rows below,
            they read as things you add before choosing one.
          */}
          <div className="grid grid-cols-2 gap-5">
            <CompanyReceipt value={company} onChange={setCompany} online={online} />
            <Coupon value={coupon} onChange={setCoupon} locked={pendingOrderId === context.orderId} />
          </div>

          {methods.map((method) => {
            const why = unavailable(method.method)
            const disabled = why !== null || busy !== null
            return (
              <button
                key={method.id}
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (method.method === 'cash') void onCash()
                  else if (method.method === 'card') setTerminalOpen(true)
                  else void onQPay()
                }}
                className="tap flex items-center gap-8 rounded-[var(--radius-tile)] border-4 border-[var(--color-line)] bg-[var(--color-paper-raised)] p-6 text-left shadow-[var(--shadow-lift)] disabled:opacity-45 disabled:shadow-none active:border-[var(--color-brand)] active:bg-[var(--color-brand-soft)]"
              >
                {/*
                  Decorative: the label beside it already names the method, and a
                  screen reader that read "golomt.png" as well would say the same
                  thing twice.
                */}
                <img
                  src={METHOD_ART[method.method]}
                  alt=""
                  aria-hidden="true"
                  className="size-40 shrink-0 rounded-[var(--radius-card)] object-cover shadow-[var(--shadow-lift)]"
                />

                <span className="flex min-w-0 flex-1 flex-col gap-2">
                  <span className="display-upright text-[length:var(--text-kiosk-lg)]">{method.label}</span>
                  {why !== null && (
                    <span className="display-upright self-start rounded-[var(--radius-pill)] bg-[var(--color-line)] px-5 py-2 text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-soft)] uppercase">
                      {why}
                    </span>
                  )}
                </span>

                {busy === method.method ? (
                  <Spinner />
                ) : (
                  <span className="grid size-20 shrink-0 place-items-center rounded-full bg-[var(--color-brand)] text-[var(--color-brand-ink)]">
                    <Icon name="forward" className="size-9" />
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/*
        One control, and it is off while a charge is in flight. A back button
        beside a terminal that is waiting on a PIN is the one press on this
        screen that can strand a payment.
      */}
      <ActionBar>
        <Button
          variant="text"
          className="text-white/60"
          disabled={busy !== null}
          onClick={() => {
            /*
             * Leaving without paying. The reservation goes back before the
             * customer has finished turning around, rather than sitting on the
             * shop's stock until its TTL runs out.
             */
            if (pendingOrderId !== null) {
              abandonOrder(pendingOrderId)
              setPendingOrderId(null)
            }
            setStep('checkout')
          }}
        >
          <Icon name="back" className="size-8" />
          {t('common.back')}
        </Button>
      </ActionBar>

      <TerminalModal open={terminalOpen} amount={totals.gross} onCancel={() => setTerminalOpen(false)} onPay={onCard} />

      {qpay !== null && (
        <QPayModal
          qrText={qpay.qrText}
          paymentId={qpay.paymentId}
          orderId={context.orderId}
          amount={totals.gross}
          onLiveChange={setQpayLive}
          onSettled={() => {
            setQpay(null)
            finish(context.orderId)
          }}
          onFailed={(reason) => {
            setQpay(null)
            setProblem(reason)
          }}
          onCancel={async () => {
            await cancelQPay(qpay.paymentId)
            setQpay(null)
          }}
          /*
            A fresh code, and the old one cancelled first.
            
            The order matters and it is the whole risk in this feature: two live
            invoices against one order is a customer who scans the stale code
            and pays an invoice the kiosk has stopped watching. Cancelling
            before issuing means the worst case is a customer who scans a code
            that is already dead — which is the state they were in anyway, and
            which the sheet now names.
            
            A new `paymentId` because the old one is spent: the server models
            one row per payment, and reusing the id would be an upsert onto a
            payment that has been cancelled.
          */
          onRenew={async () => {
            await cancelQPay(qpay.paymentId)
            const paymentId = uuid()
            const result = await startQPay(payContext, paymentId)
            if (!result.ok) {
              setQpay(null)
              setProblem(result.refusal)
              return
            }
            setQpay({ qrText: result.qrText, paymentId })
          }}
        />
      )}
    </div>
  )
}
